// Generated by Claude Code
import { useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Copy, Pencil, RotateCcwKey, Trash2 } from 'lucide-react'
import { useUserState } from '#core/state/UserState.ts'
import { AuthUserElevated, PermissionAction, PermissionService } from '#core/entities/auth/roles/Permission.ts'
import type ApiUser from '#core/entities/auth/api-users/ApiUser.ts'
import type RoleDetail from '#core/entities/auth/roles/RoleDetail.ts'
import { ConfirmModal } from '#components/primitives/components/confirm-modal.tsx'
import { SettingsTable } from '#components/domain/operations/settings/users-teams/settings-table.tsx'
import type { ApiUsersState } from './api-users-state.ts'
import { ApiUserEditModal } from './api-user-edit-modal.tsx'
import { ApiUserSecretModal } from './api-user-secret-modal.tsx'

interface ApiUsersTabProps {
  state: ApiUsersState
  searchQuery: string
  roles: RoleDetail[]
}

/** API users table plus its create/edit, confirm and one-time-secret dialogs. */
export function ApiUsersTab({ state, searchQuery, roles }: ApiUsersTabProps) {
  const { t } = useTranslation('settings')
  const userState = useUserState()

  const isSuperUser = userState.isSuperAdmin
  // The backend omits the role fields from the payload for callers without Users Edit.
  const showRole = userState.hasPermission(PermissionService.AUTH_USERS, PermissionAction.UPDATE)
  const canRotate = userState.hasPermission(PermissionService.AUTH_USERS, AuthUserElevated.ROTATE_API_USER_SECRETS)

  useEffect(() => {
    void state.ensureLoaded()
  }, [state])

  const apiUsers = state.filtered(searchQuery)
  const confirm = state.confirm

  return (
    <>
      {state.loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status" />
        </div>
      ) : state.loadError ? (
        <div className="alert alert-danger" role="alert">{t('usersTeams.loadError')}</div>
      ) : (
        <SettingsTable
          isEmpty={apiUsers.length === 0}
          emptyLabel={t('usersTeams.noApiUsers')}
          countLabel={t('usersTeams.apiUsersShowing', { count: apiUsers.length, total: state.apiUsers.length })}
          headers={
            <>
              <th>{t('shared.fields.name')}</th>
              <th>{t('usersTeams.apiUsers.clientId')}</th>
              {showRole && <th>{t('shared.fields.role')}</th>}
              <th>{t('shared.fields.enabled')}</th>
              <th>{t('usersTeams.apiUsers.lastUsed')}</th>
              <th>{t('shared.fields.createdAt')}</th>
              <th className="text-end">{t('shared.fields.actions')}</th>
            </>
          }
        >
          {apiUsers.map(apiUser => (
            <ApiUserRow
              key={apiUser.id}
              apiUser={apiUser}
              showRole={showRole}
              canRotate={canRotate}
              canManage={isSuperUser}
              onCopy={value => state.copy(value)}
              onToggleEnabled={enabled => void state.setEnabled(apiUser.id, enabled)}
              onRotate={() => state.requestConfirm('rotate', apiUser.id)}
              onEdit={() => state.openEdit(apiUser.id)}
              onDelete={() => state.requestConfirm('delete', apiUser.id)}
            />
          ))}
        </SettingsTable>
      )}

      <ApiUserEditModal
        isOpen={state.editOpen}
        apiUser={state.editing}
        roles={roles}
        onClose={() => state.closeEdit()}
        onCreated={created => state.onCreated(created)}
        onSaved={() => void state.reload()}
      />
      <ConfirmModal
        isOpen={confirm !== null}
        title={confirm?.type === 'rotate' ? t('usersTeams.apiUsers.rotateConfirm.title') : t('usersTeams.apiUsers.deleteConfirm.title')}
        message={confirm && (
          <Trans
            i18nKey={confirm.type === 'rotate' ? 'usersTeams.apiUsers.rotateConfirm.message' : 'usersTeams.apiUsers.deleteConfirm.message'}
            ns="settings"
            values={{ name: confirm.name }}
          />
        )}
        warning={confirm?.type === 'rotate' ? t('usersTeams.apiUsers.rotateConfirm.warning') : undefined}
        confirmLabel={confirm?.type === 'rotate' ? t('usersTeams.apiUsers.rotateConfirm.confirm') : t('shared.actions.delete')}
        cancelLabel={t('shared.actions.cancel')}
        genericErrorLabel={confirm?.type === 'rotate' ? t('usersTeams.apiUsers.rotateError') : t('usersTeams.deleteError')}
        variant="danger"
        onClose={() => state.cancelConfirm()}
        onConfirm={() => state.executeConfirm()}
      />
      <ApiUserSecretModal issued={state.issued} onCopy={value => state.copy(value)} onClose={() => state.dismissIssued()} />
    </>
  )
}

interface ApiUserRowProps {
  apiUser: ApiUser
  showRole: boolean
  canRotate: boolean
  canManage: boolean
  onCopy: (value: string) => void
  onToggleEnabled: (enabled: boolean) => void
  onRotate: () => void
  onEdit: () => void
  onDelete: () => void
}

function ApiUserRow({ apiUser, showRole, canRotate, canManage, onCopy, onToggleEnabled, onRotate, onEdit, onDelete }: ApiUserRowProps) {
  const { t } = useTranslation('settings')

  const customPermissionCount = apiUser.personal_permissions?.length ?? 0

  return (
    <tr>
      <td className="fw-medium">{apiUser.name}</td>
      <td>
        <span className="d-inline-flex align-items-center gap-1">
          <code className="text-body">{apiUser.identifier}</code>
          <button
            type="button"
            className="btn btn-sm btn-icon p-1"
            title={t('shared.actions.copy')}
            aria-label={t('shared.actions.copy')}
            onClick={() => onCopy(apiUser.identifier)}
          >
            <Copy size={14} className="text-secondary" />
          </button>
        </span>
      </td>
      {showRole && (
        <td>
          {apiUser.role_name ? (
            <span className="badge bg-primary bg-opacity-10 text-primary">{apiUser.role_name}</span>
          ) : customPermissionCount > 0 ? (
            <span className="badge" style={{ backgroundColor: '#ffd7c4', color: '#7a3b1d' }}>
              {t('usersTeams.userTable.customPermissions')} · {customPermissionCount}
            </span>
          ) : (
            <span className="text-secondary">—</span>
          )}
        </td>
      )}
      <td>
        <div className="form-check form-switch mb-0 d-flex align-items-center">
          <input
            type="checkbox"
            className="form-check-input"
            role="switch"
            checked={apiUser.enabled}
            disabled={!canManage}
            aria-label={t('shared.fields.enabled')}
            onChange={e => onToggleEnabled(e.target.checked)}
          />
        </div>
      </td>
      <td className="text-secondary">
        {apiUser.last_logged_in ? apiUser.formatDateTime(apiUser.last_logged_in) : t('usersTeams.apiUsers.never')}
      </td>
      <td className="text-secondary">{apiUser.formatDate(apiUser.created_at)}</td>
      <td className="text-end text-nowrap">
        {canRotate && (
          <button
            type="button"
            className="btn btn-sm btn-icon p-1"
            title={t('usersTeams.apiUsers.rotateSecret')}
            aria-label={t('usersTeams.apiUsers.rotateSecret')}
            onClick={onRotate}
          >
            <RotateCcwKey size={16} className="text-secondary" />
          </button>
        )}
        {canManage && (
          <>
            <button type="button" className="btn btn-sm btn-icon p-1 ms-1" title={t('shared.actions.edit')} onClick={onEdit}>
              <Pencil size={16} className="text-secondary" />
            </button>
            <button type="button" className="btn btn-sm btn-icon p-1 ms-1" title={t('shared.actions.delete')} onClick={onDelete}>
              <Trash2 size={16} className="text-danger" />
            </button>
          </>
        )}
      </td>
    </tr>
  )
}
